import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ConfigApi from "../../utils/api/config/config.api";
import { useMonKycQuery } from "../../utils/api/kyc/kyc.api";
import { Projet } from "../../utils/api/launchCampagn/projet.type";
import {
  useAddRetraitMutation,
  useBaremeQuery,
  useConfirmerMutation,
  useRenvoyerCodeMutation,
} from "../../utils/api/retrait/retrait.api";
import {
  CANAUX_VERSEMENT,
  pushDisponible,
  RetraitMode,
} from "../../utils/api/retrait/retrait.type";
import { COLORS } from "../../utils/constants";
import { getRoutePath } from "../routes/routes";
import { formatMontant, useModalActions } from "../../utils/utils";
import PhoneInput from "../shared/PhoneInput";
import "./InvestissementModal.css";

const erreurDe = (res: any, defaut: string) =>
  res?.error?.data?.message ||
  (typeof res?.error?.data === "object"
    ? Object.values(res?.error?.data || {})[0]
    : null) ||
  defaut;

// Demande de retrait en deux temps : la destination, puis le code recu par
// email. Le versement n'est declenche qu'apres ce second facteur, parce que
// c'est le porteur lui-meme qui met de l'argent en mouvement.
function DebitModal({ modalId, projet }: { modalId: string; projet: Projet }) {
  const ma = useModalActions(modalId);
  const navigate = useNavigate();
  const { data: config } = ConfigApi.useGetConfigQuery();
  // On interroge le KYC a l'ouverture : inutile de laisser quelqu'un
  // remplir tout le formulaire pour lui refuser l'envoi ensuite.
  const { data: kyc, isLoading: kycEnCours } = useMonKycQuery();
  const statutKyc = (kyc as any)?.statut || null;
  const identiteVerifiee = statutKyc === "valide";
  const [addRetrait, { isLoading: envoiEnCours }] = useAddRetraitMutation();
  const [confirmer, { isLoading: confirmationEnCours }] = useConfirmerMutation();
  const [renvoyerCode] = useRenvoyerCodeMutation();

  const [etape, setEtape] = useState<"destination" | "code">("destination");
  const [slug, setSlug] = useState("");
  const [code, setCode] = useState("");
  const [erreur, setErreur] = useState("");

  const [mode, setMode] = useState<RetraitMode>("mobile_money");
  const [pays, setPays] = useState("SN");
  const [canal, setCanal] = useState(CANAUX_VERSEMENT.SN.canaux[0].slug);
  const [numero, setNumero] = useState("");
  const [titulaire, setTitulaire] = useState("");
  const [banque, setBanque] = useState("");
  const [iban, setIban] = useState("");
  const [swift, setSwift] = useState("");
  const [numeroCarte, setNumeroCarte] = useState("");
  const [montant, setMontant] = useState("");

  const disponible = Number(projet?.montant_total_restant || 0);

  // Le bareme depend de la destination : un retrait Wave Senegal et un
  // virement bancaire n'ont ni le meme minimum ni la meme commission. Le
  // serveur resout la regle la plus precise ; on ne recalcule rien ici.
  const { data: bareme } = useBaremeQuery({
    mode,
    ...(mode === "mobile_money" ? { pays, canal } : {}),
  });

  // Tant que le bareme n'est pas revenu, les valeurs globales servent de
  // repli : elles sont deja chargees et donnent un ordre de grandeur juste.
  const regle = useMemo(
    () => ({
      pourcentage: Number(
        bareme?.pourcentage ?? config?.retrait_pourcentage ?? 0
      ) || 0,
      frais_fixe: Number(bareme?.frais_fixe ?? config?.retrait_frais_fixe ?? 0) || 0,
      minimum: Number(bareme?.minimum ?? config?.retrait_minimum ?? 0) || 0,
      plafond: Number(bareme?.plafond ?? config?.retrait_plafond ?? 0) || 0,
    }),
    [bareme, config]
  );

  // Le montant commande tout le reste : tant qu'il n'est pas valide, les
  // champs suivants restent fermes et le bouton grise. Inutile de choisir un
  // operateur pour un montant qu'on va refuser.
  const { erreurMontant, montantValide } = useMemo(() => {
    const brut = montant.trim();
    if (!brut) return { erreurMontant: null, montantValide: false };
    const m = parseFloat(brut);
    if (isNaN(m) || m <= 0)
      return { erreurMontant: "Saisissez un montant valide.", montantValide: false };
    const mini = regle.minimum;
    const plafond = regle.plafond;
    if (mini && m < mini)
      return {
        erreurMontant: `Le minimum de retrait est de ${formatMontant(mini)}.`,
        montantValide: false,
      };
    if (plafond && m > plafond)
      return {
        erreurMontant: `Le maximum par demande est de ${formatMontant(plafond)}.`,
        montantValide: false,
      };
    if (m > disponible)
      return {
        erreurMontant: `Vous ne disposez que de ${formatMontant(disponible)}.`,
        montantValide: false,
      };
    return { erreurMontant: null, montantValide: true };
  }, [montant, regle, disponible]);

  // Commission deduite : le porteur demande X et recoit X moins la commission.
  const { commission, net } = useMemo(() => {
    const m = parseFloat(montant || "0") || 0;
    const c = Math.min(
      Math.round((m * regle.pourcentage) / 100 + regle.frais_fixe),
      m
    );
    return { commission: c, net: Math.max(m - c, 0) };
  }, [montant, regle]);

  useEffect(() => {
    const premier = CANAUX_VERSEMENT[pays]?.canaux?.[0]?.slug;
    if (premier) setCanal(premier);
  }, [pays]);

  const reinitialiser = () => {
    setEtape("destination");
    setSlug("");
    setCode("");
    setErreur("");
    setMontant("");
    // Un numero de carte n'a pas a survivre a la demande qui l'a utilise.
    setNumeroCarte("");
  };

  const envoyerDemande = async () => {
    setErreur("");
    const m = parseFloat(montant || "0");
    if (!m || m <= 0) return setErreur("Saisissez un montant.");
    if (m > disponible)
      return setErreur(
        `Montant supérieur au disponible (${formatMontant(disponible)}).`
      );
    if (mode === "mobile_money" && !numero)
      return setErreur("Saisissez le numéro qui recevra l'argent.");
    if (mode === "bancaire" && !(titulaire.trim() && banque.trim() && iban.trim()))
      return setErreur("Titulaire, banque et IBAN sont requis pour un virement.");
    if (mode === "carte") {
      if (!(titulaire.trim() && banque.trim() && numeroCarte.trim()))
        return setErreur(
          "Titulaire, banque émettrice et numéro de carte sont requis."
        );
    }

    const res: any = await addRetrait({
      projet: projet.id,
      montant: m,
      mode,
      ...(mode === "mobile_money"
        ? { pays, canal, numero }
        : mode === "carte"
        ? {
            banque_titulaire: titulaire.trim(),
            banque_nom: banque.trim(),
            carte_numero: numeroCarte.replace(/\D/g, ""),
          }
        : {
            banque_titulaire: titulaire.trim(),
            banque_nom: banque.trim(),
            banque_iban: iban.trim(),
            banque_swift: swift.trim(),
          }),
    });
    if (res?.error) {
      // Identité non vérifiée : on emmène vers le formulaire plutôt que
      // d'afficher une erreur sans issue.
      if (res.error?.data?.code === "kyc_requis") {
        // Filet de securite : le serveur reste seul juge, meme si l'ouverture
        // de la modale a deja filtre le cas.
        const statutRenvoye = res.error.data.kyc_statut;
        ma.hide();
        Swal.fire({
          icon: "info",
          title: "Vérification d'identité requise",
          text: res.error.data.message,
          showCancelButton: statutRenvoye === "en_attente",
          cancelButtonText: "Fermer",
          confirmButtonText:
            statutRenvoye === "en_attente"
              ? "Voir mon dossier"
              : "Vérifier mon identité",
          confirmButtonColor: COLORS.green,
        }).then((r) => {
          if (r.isConfirmed) navigate(getRoutePath("VerificationIdentite"));
        });
        return;
      }
      return setErreur(erreurDe(res, "La demande n'a pas pu être enregistrée."));
    }
    setSlug(res.data.slug);
    setEtape("code");
  };

  const validerCode = async () => {
    setErreur("");
    if (!code) return setErreur("Saisissez le code reçu par email.");
    const res: any = await confirmer({ slug, code });
    if (res?.error) return setErreur(erreurDe(res, "Code incorrect."));
    ma.hide();
    const auto = pushDisponible(mode, pays);
    Swal.fire({
      icon: "success",
      title: auto ? "Versement lancé" : "Demande confirmée",
      text: auto
        ? `${formatMontant(net)} sont en cours d'envoi vers ${numero}.`
        : "Un administrateur procédera au versement.",
      iconColor: COLORS.green,
      confirmButtonColor: COLORS.green,
    }).then(reinitialiser);
  };

  return (
    <div
      className="modal fade"
      id={modalId}
      data-bs-backdrop="static"
      data-backdrop="false"
      data-bs-keyboard="false"
      aria-labelledby={`label${modalId}`}
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content custom-modal-investissement">
          <div className="modal-header modal-header-investissement">
            <h5 className="modal-title" id={`label${modalId}`}>
              Demander un retrait
            </h5>
          </div>
          <div className="modal-body modal-invessetissement-body">
            {!kycEnCours && !identiteVerifiee && (
              <>
                <div className="alert alert-warning">
                  {statutKyc === "en_attente"
                    ? "Votre vérification d'identité est en cours d'examen. Vous pourrez demander un retrait dès qu'elle sera acceptée."
                    : statutKyc === "refuse"
                    ? "Votre vérification d'identité a été refusée. Corrigez votre dossier pour pouvoir retirer."
                    : "Vérifiez votre identité avant de demander un retrait."}
                </div>
                <div className="modal-investissement-montants-button-container mt-3">
                  <button
                    type="button"
                    className="btn modal-investissement-montants-btn-annuler"
                    onClick={() => ma.hide()}
                  >
                    Fermer
                  </button>
                  <button
                    type="button"
                    className="btn modal-investissement-montants-btn-continuer"
                    onClick={() => {
                      ma.hide();
                      navigate(getRoutePath("VerificationIdentite"));
                    }}
                  >
                    {statutKyc === "en_attente"
                      ? "Voir mon dossier"
                      : "Vérifier mon identité"}
                  </button>
                </div>
              </>
            )}

            {identiteVerifiee && etape === "destination" && (
              <>
                <div className="modal-investissement-montants mb-2">
                  <span>
                    Disponible : <b>{formatMontant(disponible)}</b>
                  </span>
                </div>

                <div className="modal-investissement-montants-input">
                  <input
                    type="number"
                    className="form-control-contribution-steps form-control form-control-lg"
                    value={montant}
                    onChange={(e) => setMontant(e.target.value)}
                    placeholder="Montant à retirer (FCFA)"
                  />
                  {erreurMontant && (
                    <div className="alert alert-danger sia-alert-danger closer mt-2">
                      {erreurMontant}
                    </div>
                  )}
                  {(regle.minimum > 0 || regle.plafond > 0) && (
                    <div
                      className="mt-1"
                      style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}
                    >
                      {regle.minimum > 0 && `Minimum ${formatMontant(regle.minimum)}`}
                      {regle.minimum > 0 && regle.plafond > 0 && " · "}
                      {regle.plafond > 0 && `maximum ${formatMontant(regle.plafond)}`}
                      {" pour la destination choisie."}
                    </div>
                  )}
                </div>

                <div className="modal-investissement-montants-input">
                  <label className="modal-investissement-description mb-1">
                    Comment souhaitez-vous être payé ?
                  </label>
                  <select
                    className="form-select"
                    value={mode}
                    disabled={!montantValide}
                    onChange={(e) => setMode(e.target.value as RetraitMode)}
                  >
                    <option value="mobile_money">Mobile money</option>
                    <option value="bancaire">Virement bancaire</option>
                    <option value="carte">Virement par carte</option>
                  </select>
                </div>

                {mode === "mobile_money" && (
                  <>
                    <div className="modal-investissement-montants-input">
                      <select
                        className="form-select"
                        value={pays}
                        disabled={!montantValide}
                        onChange={(e) => setPays(e.target.value)}
                      >
                        {Object.values(CANAUX_VERSEMENT).map((p) => (
                          <option key={p.code} value={p.code}>
                            {p.nom}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="modal-investissement-montants-input">
                      <select
                        className="form-select"
                        value={canal}
                        disabled={!montantValide}
                        onChange={(e) => setCanal(e.target.value)}
                      >
                        {CANAUX_VERSEMENT[pays]?.canaux.map((c) => (
                          <option key={c.slug} value={c.slug}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <PhoneInput
                      placeholder="Numéro qui recevra l'argent"
                      value={numero}
                      onChnage={setNumero}
                      name="numero"
                      country={pays.toLowerCase()}
                      disabled={!montantValide}
                    />
                    {!pushDisponible(mode, pays) && (
                      <div className="alert alert-warning">
                        Le versement automatique n'est pas encore disponible
                        pour ce pays : un administrateur effectuera le paiement.
                      </div>
                    )}
                  </>
                )}

                {mode === "bancaire" && (
                  <>
                    <div className="modal-investissement-montants-input">
                      <input
                        type="text"
                        className="form-control-contribution-steps form-control form-control-lg"
                        placeholder="Titulaire du compte"
                        value={titulaire}
                        disabled={!montantValide}
                        onChange={(e) => setTitulaire(e.target.value)}
                      />
                    </div>
                    <div className="modal-investissement-montants-input">
                      <input
                        type="text"
                        className="form-control-contribution-steps form-control form-control-lg"
                        placeholder="Nom de la banque"
                        value={banque}
                        disabled={!montantValide}
                        onChange={(e) => setBanque(e.target.value)}
                      />
                    </div>
                    <div className="modal-investissement-montants-input">
                      <input
                        type="text"
                        className="form-control-contribution-steps form-control form-control-lg"
                        placeholder="IBAN ou numéro de compte"
                        value={iban}
                        disabled={!montantValide}
                        onChange={(e) => setIban(e.target.value)}
                      />
                    </div>
                    <div className="modal-investissement-montants-input">
                      <input
                        type="text"
                        className="form-control-contribution-steps form-control form-control-lg"
                        placeholder="Code SWIFT / BIC (facultatif)"
                        value={swift}
                        disabled={!montantValide}
                        onChange={(e) => setSwift(e.target.value)}
                      />
                    </div>
                    <div className="alert alert-warning">
                      Les virements bancaires sont effectués à la main par un
                      administrateur : comptez un délai de traitement.
                    </div>
                  </>
                )}

                {mode === "carte" && (
                  <>
                    <div className="modal-investissement-montants-input">
                      <input
                        type="text"
                        className="form-control-contribution-steps form-control form-control-lg"
                        placeholder="Titulaire de la carte"
                        autoComplete="cc-name"
                        value={titulaire}
                        disabled={!montantValide}
                        onChange={(e) => setTitulaire(e.target.value)}
                      />
                    </div>
                    <div className="modal-investissement-montants-input">
                      <input
                        type="text"
                        className="form-control-contribution-steps form-control form-control-lg"
                        placeholder="Banque émettrice de la carte"
                        value={banque}
                        disabled={!montantValide}
                        onChange={(e) => setBanque(e.target.value)}
                      />
                    </div>
                    <div className="modal-investissement-montants-input">
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="cc-number"
                        className="form-control-contribution-steps form-control form-control-lg"
                        placeholder="Numéro de la carte"
                        value={numeroCarte}
                        disabled={!montantValide}
                        // Groupes de quatre chiffres, comme sur la carte : une
                        // faute de frappe se repere a l'oeil.
                        onChange={(e) =>
                          setNumeroCarte(
                            e.target.value
                              .replace(/\D/g, "")
                              .replace(/(\d{4})(?=\d)/g, "$1 ")
                          )
                        }
                      />
                    </div>
                    <div className="alert alert-warning">
                      Les virements par carte sont effectués à la main par un
                      administrateur : comptez un délai de traitement. Votre
                      numéro de carte est chiffré et n'est visible que de
                      l'administrateur au moment du versement.
                    </div>
                  </>
                )}

                {parseFloat(montant || "0") > 0 && (
                  <div className="modal-investissement-confirm-para-container">
                    <div>Montant demandé : {formatMontant(montant)}</div>
                    <div>Commission : −{formatMontant(commission)}</div>
                    <div>
                      <b>Vous recevrez : {formatMontant(net)}</b>
                    </div>
                  </div>
                )}
              </>
            )}

            {identiteVerifiee && etape === "code" && (
              <>
                <div className="modal-investissement-description mb-3">
                  Un code de confirmation vous a été envoyé par email. Saisissez-le
                  pour valider le versement de <b>{formatMontant(net)}</b>
                  {mode === "mobile_money" ? (
                    <>
                      {" "}
                      vers le <b>{numero}</b>.
                    </>
                  ) : mode === "carte" ? (
                    <>
                      {" "}
                      vers la carte <b>•••• {numeroCarte.replace(/\D/g, "").slice(-4)}</b>.
                    </>
                  ) : (
                    "."
                  )}
                </div>
                <div className="modal-investissement-montants-input">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    className="form-control-contribution-steps form-control form-control-lg"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Code à 6 chiffres"
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={async () => {
                    await renvoyerCode(slug);
                    setErreur("");
                    Swal.fire({
                      icon: "success",
                      title: "Nouveau code envoyé",
                      iconColor: COLORS.green,
                      showConfirmButton: false,
                      timer: 1500,
                    });
                  }}
                >
                  Renvoyer le code
                </button>
              </>
            )}

            {erreur && (
              <div className="alert alert-danger sia-alert-danger closer mt-2">
                {erreur}
              </div>
            )}

            {identiteVerifiee && (
            <div className="modal-investissement-montants-input-bouton">
              <div className="modal-investissement-montants-button-container">
                <button
                  type="button"
                  className="btn modal-investissement-montants-btn-annuler"
                  onClick={() => {
                    ma.hide();
                    reinitialiser();
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={
                    envoiEnCours ||
                    confirmationEnCours ||
                    (etape === "destination" && !montantValide)
                  }
                  className="btn modal-investissement-montants-btn-continuer"
                  onClick={etape === "destination" ? envoyerDemande : validerCode}
                >
                  {envoiEnCours || confirmationEnCours
                    ? "Veuillez patienter…"
                    : etape === "destination"
                    ? "Continuer"
                    : "Valider le versement"}
                </button>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DebitModal;
